import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  bufferCount,
  combineLatest,
  filter,
  first,
  from,
  map,
  mergeMap,
  of,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Injector,
  Input,
  OnInit,
  Output,
  ɵcreateInjector as createInjector,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  TabloElementGroupList,
  TabloFaceToPerson,
  TabloGroupConfig,
  TabloGroupMemberType,
  TabloMember,
  TabloPlaceHolder,
} from '@ui/tablo-generator/types';

import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { FontSyncService } from '@ui/tablo-generator/child-name/font-sync.service';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ImageContainer } from '../../../../../../../../graphql/apps/photographer/src/graphql';
import { ZalamoObject } from '@ui/orders/graphql';
import { FaceStrongDetectionService } from 'ZalamoLibrary/src/lib/zalamo/face-detection/face-strong-detection.service';
import { PublicSessionOrganizationMemberRole } from '../../../../../../../zalamo-support/src/graphql';
import { GetPeopleInGroupGQL } from '../../../image-management/graphql';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';
import { TabloSinglePhotoEditorComponent } from '@ui/tablo-generator/tablo-studio/tablo-single-photo-editor/tablo-single-photo-editor.component';
import { FontLoaderService } from '@ui/fonts-display/font-loader.service';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import {
  firstValueFrom,
  RxjsOperators,
} from 'ZalamoLibrary/src/lib/core/operators/RxjsOperators';
import { FullscreenService } from '@ui/fullscreen/fullscreen.service';
import { UploadService } from 'ZalamoLibrary/src/lib/upload/upload.service';
import {
  GetPublicSessionOrganizationMembersGQL,
  UpdatePublicSessionOrganizationMemberGQL,
} from '../../../../../../../photographer-panel/src/graphql';

type ChildLayoutItem = TabloMember & {
  type: 'child';
  sourceIndex?: number;
  renderKey?: string;
  row?: number;
  itemIndex?: number;
  itemsInRow?: number;
  rowsCount?: number;
};

type PlaceholderLayoutItem = {
  isEmpty: true;
  index: number;
  placeholderIndex: number;
  placeHolderSize: number;
  isPlaceholder: true;
  childPositionDistortion: {
    x: number;
    y: number;
  };
  childOriginTransform: {
    x: number;
    y: number;
  };
  type: 'placeholder';
  renderKey?: string;
  row?: number;
  itemIndex?: number;
  itemsInRow?: number;
  rowsCount?: number;
};

type EmptyLayoutItem = {
  isEmpty: true;
  firstName: string;
  lastName: string;
  type: 'empty';
  tabloMemberId: string | null;
  roles: PublicSessionOrganizationMemberRole[];
  photo: null;
  index: number;
  isSpine?: boolean;
  layoutWidth?: number;
  renderKey?: string;
  row?: number;
  itemIndex?: number;
  itemsInRow?: number;
  rowsCount?: number;
};

type GroupLayoutItem =
  ChildLayoutItem | PlaceholderLayoutItem | EmptyLayoutItem;
type GroupSourceItem = ChildLayoutItem | PlaceholderLayoutItem;
export interface TabloGroupMissingImageDebug {
  groupElementId: string | null;
  groupType: TabloGroupMemberType | ZalamoObject;
  contextId: string;
  imagesContext: ZalamoObject;
  expectedImages: number;
  loadedImages: number;
  missingImages: Array<{
    tabloMemberId: string | null;
    firstName: string | null;
    lastName: string | null;
    photoId: string | null;
    photoGroupId: string | null;
    photoSource: 'faceMatch' | 'memberPhoto' | 'none';
    sourceIndex?: number;
    row?: number;
    itemIndex?: number;
  }>;
}
type SpineBoundarySide = GroupSourceItem['type'] | 'empty';
type RowSpineBoundary = {
  boundary: number;
  isChildColumnSeam: boolean;
};
type RowLayoutSegment = {
  item: GroupLayoutItem;
  rowStart: number;
  rowEnd: number;
};

const SPINE_BOUNDARY_EPSILON = 0.0001;

const createPlaceholderLayoutItem = ({
  index,
  size,
}: TabloPlaceHolder): PlaceholderLayoutItem => ({
  isEmpty: true,
  index,
  placeholderIndex: index,
  placeHolderSize: size,
  isPlaceholder: true,
  childPositionDistortion: { x: 0, y: 0 },
  childOriginTransform: { x: 0, y: 0 },
  type: 'placeholder',
  renderKey: `placeholder-${index}`,
});

const createEmptyLayoutItem = (
  overrides: Partial<EmptyLayoutItem>,
): EmptyLayoutItem => ({
  isEmpty: true,
  firstName: '',
  lastName: '',
  type: 'empty',
  tabloMemberId: null,
  roles: [] as PublicSessionOrganizationMemberRole[],
  photo: null,
  index: 0,
  layoutWidth: 1,
  ...overrides,
});

export const getLayoutItemSize = (
  item: Pick<GroupLayoutItem, 'type'> & {
    placeHolderSize?: number;
    layoutWidth?: number;
  },
) =>
  item.type === 'placeholder'
    ? (item.placeHolderSize ?? 1)
    : (item.layoutWidth ?? 1);

export const getRowSize = (
  row: Array<
    Pick<GroupLayoutItem, 'type'> & {
      placeHolderSize?: number;
      layoutWidth?: number;
    }
  >,
) =>
  row.reduce(
    (previousValue, currentValue) =>
      previousValue + getLayoutItemSize(currentValue),
    0,
  );

const normalizeSpineBoundary = (boundary: number) => {
  const rounded = Math.round(boundary);

  return Math.abs(boundary - rounded) < SPINE_BOUNDARY_EPSILON
    ? rounded
    : Number(boundary.toFixed(4));
};

const areSameSpineBoundary = (left: number, right: number) =>
  Math.abs(left - right) < SPINE_BOUNDARY_EPSILON;

export const groupIntoRows =
  (v: Pick<TabloGroupConfig, 'itemsPerRow'>) =>
  (previousValue: GroupSourceItem[][], currentValue: GroupSourceItem) => {
    const currentElementSize = getLayoutItemSize(currentValue);
    const currentRow = previousValue[previousValue.length - 1];
    const currentRowSize = getRowSize(currentRow);
    if (currentRowSize + currentElementSize <= v.itemsPerRow) {
      currentRow.push(currentValue);
      return previousValue;
    } else {
      previousValue.push([currentValue]);
      return previousValue;
    }
  };

export const getRowPadding = (
  rowSize: number,
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
) => {
  const remaining = Math.max(v.itemsPerRow - rowSize, 0);

  switch (v.align) {
    case 'right':
      return {
        leftPad: remaining,
        rightPad: 0,
      };
    case 'center':
      return {
        leftPad: Math.floor(remaining / 2),
        rightPad: remaining - Math.floor(remaining / 2),
      };
    case 'left':
    default:
      return {
        leftPad: 0,
        rightPad: remaining,
      };
  }
};

const getRowSpineBoundaryDetails = (
  row: GroupSourceItem[],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
): RowSpineBoundary[] => {
  const { leftPad, rightPad } = getRowPadding(getRowSize(row), v);
  const segments: Array<{
    start: number;
    end: number;
    type: SpineBoundarySide;
  }> = [];
  let cursor = 0;

  const pushSegment = (width: number, type: SpineBoundarySide) => {
    if (width <= SPINE_BOUNDARY_EPSILON) {
      return;
    }

    const start = cursor;
    const end = normalizeSpineBoundary(start + width);
    segments.push({ start, end, type });
    cursor = end;
  };

  pushSegment(leftPad, 'empty');
  row.forEach((item) => pushSegment(getLayoutItemSize(item), item.type));
  pushSegment(rightPad, 'empty');

  const boundaries: RowSpineBoundary[] = [];
  const addBoundary = (boundary: number, isChildColumnSeam: boolean) => {
    const normalizedBoundary = normalizeSpineBoundary(boundary);
    const existing = boundaries.find((candidate) =>
      areSameSpineBoundary(candidate.boundary, normalizedBoundary),
    );

    if (existing) {
      existing.isChildColumnSeam =
        existing.isChildColumnSeam || isChildColumnSeam;
      return;
    }

    boundaries.push({
      boundary: normalizedBoundary,
      isChildColumnSeam,
    });
  };

  addBoundary(0, false);

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index];

    if (segment.type === 'empty') {
      for (
        let boundary = Math.floor(segment.start) + 1;
        boundary < segment.end - SPINE_BOUNDARY_EPSILON;
        boundary++
      ) {
        addBoundary(boundary, false);
      }
    }

    addBoundary(
      segment.end,
      segment.type === 'child' && segments[index + 1]?.type === 'child',
    );
  }

  return boundaries.sort((a, b) => a.boundary - b.boundary);
};

export const getAllowedRowSpineBoundaries = (
  row: GroupSourceItem[],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
): number[] =>
  getRowSpineBoundaryDetails(row, v).map(({ boundary }) => boundary);

export const pickGlobalSpineBoundary = (
  rows: GroupSourceItem[][],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
  targetBoundary: number,
) => {
  const clampedTarget = Math.max(0, Math.min(v.itemsPerRow, targetBoundary));
  const rowBoundaryDetails = rows.map((row) =>
    getRowSpineBoundaryDetails(row, v),
  );
  const commonBoundaries = rowBoundaryDetails.reduce<number[]>(
    (current, rowBoundaries, rowIndex) => {
      const currentRowBoundaries = rowBoundaries.map(
        ({ boundary }) => boundary,
      );
      if (rowIndex === 0) {
        return currentRowBoundaries;
      }

      return current.filter((boundary) =>
        currentRowBoundaries.some((rowBoundary) =>
          areSameSpineBoundary(boundary, rowBoundary),
        ),
      );
    },
    [],
  );
  const fallbackBoundaries = Array.from(
    { length: v.itemsPerRow + 1 },
    (_, index) => index,
  );
  const childColumnSeamBoundaries = commonBoundaries.filter((boundary) =>
    rowBoundaryDetails.some((rowBoundaries) =>
      rowBoundaries.some(
        (rowBoundary) =>
          rowBoundary.isChildColumnSeam &&
          areSameSpineBoundary(rowBoundary.boundary, boundary),
      ),
    ),
  );
  const innerChildColumnSeamBoundaries = childColumnSeamBoundaries.filter(
    (boundary) => boundary > 0 && boundary < v.itemsPerRow,
  );
  const innerCommonBoundaries = commonBoundaries.filter(
    (boundary) => boundary > 0 && boundary < v.itemsPerRow,
  );
  const innerFallbackBoundaries = fallbackBoundaries.filter(
    (boundary) => boundary > 0 && boundary < v.itemsPerRow,
  );
  const preferredBoundaries = innerChildColumnSeamBoundaries.length
    ? innerChildColumnSeamBoundaries
    : innerCommonBoundaries.length
      ? innerCommonBoundaries
      : innerFallbackBoundaries;
  const candidateBoundaries = preferredBoundaries.length
    ? preferredBoundaries
    : commonBoundaries.length
      ? commonBoundaries
      : fallbackBoundaries;

  return candidateBoundaries.reduce(
    (bestBoundary, boundary) =>
      Math.abs(boundary - clampedTarget) <
      Math.abs(bestBoundary - clampedTarget)
        ? boundary
        : bestBoundary,
    candidateBoundaries[0] ?? Math.round(clampedTarget),
  );
};

const buildRowSegments = (
  row: GroupSourceItem[],
  rowIndex: number,
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
) => {
  const { leftPad, rightPad } = getRowPadding(getRowSize(row), v);
  const segments: RowLayoutSegment[] = [];
  let rowCursor = 0;

  const pushSegment = (item: GroupLayoutItem, width: number) => {
    const rowStart = rowCursor;
    const rowEnd = rowStart + width;

    segments.push({
      item,
      rowStart,
      rowEnd,
    });
    rowCursor = rowEnd;
  };

  if (leftPad > 0) {
    pushSegment(
      createEmptyLayoutItem({
        index: rowIndex * v.itemsPerRow + 1,
        layoutWidth: leftPad,
        renderKey: `pad-${rowIndex}-left`,
      }),
      leftPad,
    );
  }

  row.forEach((item, itemIndex) => {
    const width = getLayoutItemSize(item);
    const index = rowIndex * v.itemsPerRow + rowCursor + 1;

    if (item.type === 'placeholder') {
      pushSegment(
        {
          ...item,
          index,
          placeholderIndex: item.placeholderIndex ?? item.index,
          renderKey: item.renderKey ?? `placeholder-${item.index}-${rowIndex}`,
        },
        width,
      );
      return;
    }

    pushSegment(
      {
        ...item,
        index,
        renderKey:
          item.renderKey ??
          `child-${item.tabloMemberId ?? itemIndex}-${rowIndex}`,
      },
      width,
    );
  });

  if (rightPad > 0) {
    pushSegment(
      createEmptyLayoutItem({
        index: rowIndex * v.itemsPerRow + rowCursor + 1,
        layoutWidth: rightPad,
        renderKey: `pad-${rowIndex}-right`,
      }),
      rightPad,
    );
  }

  return segments;
};

const isSpineBoundaryInsideSegment = (
  spineBoundary: number,
  segment: RowLayoutSegment,
) =>
  spineBoundary > segment.rowStart + SPINE_BOUNDARY_EPSILON &&
  spineBoundary < segment.rowEnd - SPINE_BOUNDARY_EPSILON;

const canSplitSegmentWithSpine = (item: GroupLayoutItem) =>
  item.type === 'empty';

const snapSpineBoundaryToRenderableEdge = (
  segments: RowLayoutSegment[],
  spineBoundary: number,
) => {
  const intersectedSegment = segments.find((segment) =>
    isSpineBoundaryInsideSegment(spineBoundary, segment),
  );

  if (
    !intersectedSegment ||
    canSplitSegmentWithSpine(intersectedSegment.item)
  ) {
    return spineBoundary;
  }

  const distanceToStart = spineBoundary - intersectedSegment.rowStart;
  const distanceToEnd = intersectedSegment.rowEnd - spineBoundary;

  return normalizeSpineBoundary(
    distanceToStart < distanceToEnd
      ? intersectedSegment.rowStart
      : intersectedSegment.rowEnd,
  );
};

export const buildInlineSpineRows = (
  rows: GroupSourceItem[][],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
  spineBoundary: number,
) => {
  const renderedRows = rows.map((row, rowIndex) => {
    const beforeSpine: GroupLayoutItem[] = [];
    const afterSpine: GroupLayoutItem[] = [];
    const rowSegments = buildRowSegments(row, rowIndex, v);
    const rowSpineBoundary = snapSpineBoundaryToRenderableEdge(
      rowSegments,
      spineBoundary,
    );

    rowSegments.forEach((segment) => {
      if (rowSpineBoundary >= segment.rowEnd) {
        beforeSpine.push(segment.item);
        return;
      }

      if (rowSpineBoundary <= segment.rowStart) {
        afterSpine.push(segment.item);
        return;
      }

      const beforeWidth = rowSpineBoundary - segment.rowStart;
      const afterWidth = segment.rowEnd - rowSpineBoundary;

      if (beforeWidth > 0) {
        beforeSpine.push(
          createEmptyLayoutItem({
            index: segment.item.index,
            layoutWidth: beforeWidth,
            renderKey: `${segment.item.renderKey}-before`,
          }),
        );
      }

      if (afterWidth > 0) {
        afterSpine.push(
          createEmptyLayoutItem({
            index: segment.item.index + beforeWidth,
            layoutWidth: afterWidth,
            renderKey: `${segment.item.renderKey}-after`,
          }),
        );
      }
    });

    return [
      ...beforeSpine,
      createEmptyLayoutItem({
        index: rowIndex * v.itemsPerRow + rowSpineBoundary + 0.1,
        isSpine: true,
        renderKey: `spine-${rowIndex}-left`,
      }),
      createEmptyLayoutItem({
        index: rowIndex * v.itemsPerRow + rowSpineBoundary + 0.2,
        isSpine: true,
        renderKey: `spine-${rowIndex}-right`,
      }),
      ...afterSpine,
    ];
  });

  return addRowMetadata(renderedRows);
};

export const buildPaddedRows = (
  rows: GroupSourceItem[][],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
) => {
  const renderedRows = rows.map((row, rowIndex) =>
    buildRowSegments(row, rowIndex, v).map((segment) => segment.item),
  );

  return addRowMetadata(renderedRows);
};

export const buildGroupLayoutRows = (
  items: GroupSourceItem[],
  v: Pick<TabloGroupConfig, 'align' | 'itemsPerRow'>,
  options: {
    addSpine: boolean;
    spineBoundary?: number;
  },
) => {
  const rows = items
    .reduce(groupIntoRows(v), [[]] as GroupSourceItem[][])
    .filter((row) => row.length > 0);

  if (!rows.length) {
    return [];
  }

  if (options.addSpine && typeof options.spineBoundary === 'number') {
    return buildInlineSpineRows(rows, v, options.spineBoundary);
  }

  return buildPaddedRows(rows, v);
};

const addRowMetadata = (renderedRows: GroupLayoutItem[][]) => {
  const rowsCount = renderedRows.length;

  return renderedRows.flatMap((row, rowIndex) =>
    row.map((item, itemIndex) => ({
      ...item,
      row: rowIndex + 1,
      itemIndex,
      itemsInRow: row.length,
      rowsCount,
    })),
  );
};

const addRowNumbers = (items, v) => {
  const grouped = items.reduce(groupIntoRows(v), [[]]);
  let counter = 0;
  return grouped.flatMap((row, rowIndex) =>
    row.map((item, itemIndex) => ({
      ...items[counter++],
      row: rowIndex + 1,
      itemIndex,
      itemsInRow: row.length,
      rowsCount: grouped.length,
    })),
  );
};

const intersects = <T>(a: readonly T[], b: readonly T[]): boolean => {
  // Put the shorter array in the Set to minimise memory use.
  const [shorter, longer] = a.length < b.length ? [a, b] : [b, a];
  const lookup = new Set(shorter);
  return longer.some((item) => lookup.has(item));
};

@Component({
  selector: 'lib-tablo-members-group',
  templateUrl: './tablo-members-group.component.html',
  styleUrls: ['./tablo-members-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabloMembersGroupComponent extends AliveState implements OnInit {
  config$ = new BehaviorSubject<TabloGroupConfig>(null);
  maximizePhoto$ = this.config$.pipe(
    map((config) => Boolean(config?.maximizePhoto)),
    shareReplay(1),
  );
  @Input() @Required contextId: string;
  @Input() groupElementId: string | null = null;
  originalContextId = null;
  @Input() @Required imagesContext: ZalamoObject;
  @Input() @HostBinding('class.editable') editable = false;
  @Input() isTemplate = false;
  @Output() placeholdersUpdated = new EventEmitter<TabloPlaceHolder[]>();
  @Input() isLeadingChild = false;
  @Input() leadingChildId = null;
  @Output() allImagesLoaded = new EventEmitter();
  @Output() editCrop = new EventEmitter<TabloMember>();
  placeHoldersUpdatedFromTop = false;
  handPickedUpdatedFromTop = false;
  loadedImages = new Set<string>();
  expectedImageCount = 0;
  private renderedNames: GroupLayoutItem[] = [];
  private expectedImageLoadKeys = new Set<string>();
  private imagesLoadedEmittedForSignature: string | null = null;
  wholeConfig$ = new BehaviorSubject<TabloElementGroupList>(null);
  @Input() set config(config: TabloElementGroupList) {
    this.placeHoldersUpdatedFromTop = true;
    this.handPickedUpdatedFromTop = true;
    this.loadedImages = new Set<string>();
    this.expectedImageLoadKeys = new Set<string>();
    this.expectedImageCount = 0;
    this.imagesLoadedEmittedForSignature = null;
    this.wholeConfig$.next(config);
    (config.config.font?.fontId
      ? new Promise((resolve) =>
          this.organizationService
            .getCurrentOrganization()
            .pipe(
              switchMap((organizationId) =>
                this.fontLoaderService.loadFont(
                  config.config.font.fontId,
                  config.config.font.fileName,
                  organizationId,
                ),
              ),
              first(),
              tap(resolve),
            )
            .subscribe(),
        )
      : Promise.resolve()
    ).then(() => {
      this.fontSizeFirstName.next(config.config.childFirstNameMaxFontSize);
      this.fontSizeLastName.next(config.config.childLastNameMaxFontSize);
      this.config$.next(config.config);
      this.sourceGroupIds$.next(config.imagesSource);
      this.placeholders.next(config.placeholders);
      this.placeHoldersUpdatedFromTop = false;
      this.handPicked$.next(config.handPicked);
      this.handPickedUpdatedFromTop = false;
      if (this.context === TabloGroupMemberType.teacher && !this.isTemplate) {
        this.imagesContext = ZalamoObject.PublicSessionOrganization;
        this.originalContextId = String(
          this.originalContextId === null
            ? this.contextId
            : this.originalContextId,
        );
        this.contextId = this.publicSessionOrganizationId;
      }
      if (
        this.context === TabloGroupMemberType.teacher &&
        !config.config.showDirectors
      ) {
        this.namesList$.next(
          config.list.members.filter(
            (v) =>
              (v.roles.filter(
                (r) =>
                  ![
                    PublicSessionOrganizationMemberRole.Director,
                    PublicSessionOrganizationMemberRole.ViceDirector,
                  ].includes(r),
              ).length > 0 ||
                v.groupRoles?.length > 0) &&
              config.config.attendants.includes(v.tabloMemberId),
          ),
        );
      } else {
        this.namesList$.next(
          config.list.members
            .filter(
              (v) =>
                (this.isLeadingChild &&
                  v.tabloMemberId === config.config.leadingChildId) ||
                (!this.isLeadingChild &&
                  (Array.isArray(config.config.attendants)
                    ? config.config.attendants
                    : []
                  ).includes(v.tabloMemberId)),
            )
            .filter(
              (v) =>
                !config.config.hideLeadingChild ||
                v.tabloMemberId !== this.leadingChildId,
            )
            .map((member) => {
              if (
                this.context === TabloGroupMemberType.teacher &&
                intersects(
                  [
                    PublicSessionOrganizationMemberRole.Director,
                    PublicSessionOrganizationMemberRole.ViceDirector,
                  ],
                  member.roles,
                )
              ) {
                return {
                  ...member,
                  weight: 0,
                  groupRoles: member.roles
                    .map((role) => {
                      switch (role) {
                        case PublicSessionOrganizationMemberRole.Director:
                          return 'Dyrektor';
                        case PublicSessionOrganizationMemberRole.ViceDirector:
                          return 'Wicedyrektor';
                      }
                    })
                    .filter((v) => !!v),
                };
              }
              return {
                weight: 1,
                ...member,
              };
            })
            .sort((a, b) => {
              if (config.config.directorsGoFirst) {
                return a.weight - b.weight;
              }
              return 0;
            }),
        );
      }
    });
  }

  letterSpacingMap = {
    0: 'normal',
  };
  sourceGroupIds$ = new BehaviorSubject<string[]>([]);
  sourceGroups$ = new BehaviorSubject<ImageContainer[]>([]);
  autoMode = false;
  kindsGroupedByPerson$ = new BehaviorSubject<
    Record<
      string,
      {
        firstName: string;
        lastName: string;
        photos: {
          photoId: string;
          rotation: { angle: { roll: number; yaw: number; pitch: number } };
          boxRaw: number[];
        }[];
      }
    >
  >({});
  @Input() @Required publicSessionOrganizationId: string;
  @Input() @Required publicSessionOrganizationGroupId: string;

  @Input() set images(groups: ImageContainer[]) {
    this.sourceGroups$.next(groups);
  }

  @Input() @Required projectId;
  namesList$ = new BehaviorSubject<TabloMember[]>([]);
  placeholders = new BehaviorSubject<TabloPlaceHolder[]>([]);
  private spineSnapKey$ = new BehaviorSubject<unknown>(null);

  fontSizeFirstName = new BehaviorSubject(0);
  fontSizeLastName = new BehaviorSubject(0);
  namesListenerRegistered$ = new ReplaySubject(1);
  @Input() @Required context: TabloGroupMemberType | ZalamoObject;
  @Output() handPickUpdated = new EventEmitter<
    Record<
      string,
      {
        photoId: string;
        crop: [number, number, number, number];
      }
    >
  >();
  handPicked$ = new BehaviorSubject<
    Record<
      string,
      {
        photoId: string;
        crop: [number, number, number, number];
      }
    >
  >({});

  sourcePhotos$ = combineLatest([
    this.sourceGroups$.pipe(RxjsOperators.filterTruthy()),
    this.sourceGroupIds$,
  ]).pipe(
    map(([v, config]) =>
      v
        .filter(
          ({ photoGroupId }) => config.includes(photoGroupId) || v.length === 1,
        )
        .flatMap(({ images, photoGroupId }) =>
          images.files.map((v) => ({
            ...v,
            groupId: photoGroupId,
          })),
        ),
    ),
    // /    tap((x) => console.log(x)),
  );
  names$ = combineLatest([
    this.config$.pipe(RxjsOperators.filterTruthy()),
    combineLatest([
      this.namesList$.pipe(
        tap((v) => {
          // @ts-ignore
          const teachers = v.filter((t) => !!t.avatar);
          if (teachers.length > 0) {
            this.faceMatch$.next({
              ...this.faceMatch$.value,
              ...Object.fromEntries(
                teachers.map((t) => [
                  t.tabloMemberId,
                  {
                    // @ts-ignore
                    x: t.x,
                    // @ts-ignore
                    y: t.y,
                    // @ts-ignore
                    photoId: t.photo.photoId,
                    // @ts-ignore
                    groupId: t.photo.groupId,
                    // @ts-ignore
                    width: t.photo.width,
                    // @ts-ignore
                    height: t.photo.height,
                    // @ts-ignore
                    scale: t.scale,
                    rotation: t.rotation ?? 0,
                  },
                ]),
              ),
            });
          }
        }),
        map((v) => v.map((child) => ({ ...child, type: 'child' }))),
      ),
      this.kindsGroupedByPerson$,
      this.handPicked$,
      this.sourcePhotos$,
    ]).pipe(
      map(([namesList, kindsGroupedByPerson, handPicked, sourcePhotos]) => {
        const sourcePhotosGroupedByPhotoId = sourcePhotos.reduce(
          (previousValue, currentValue) => {
            previousValue[currentValue.photo?.photoId] = currentValue.groupId;
            return previousValue;
          },
          {} as Record<string, string>,
        );
        return namesList
          .map((child) => {
            if (handPicked && handPicked[child.tabloMemberId]?.photoId) {
              return {
                ...child,
                photo: {
                  photoId: handPicked[child.tabloMemberId].photoId,
                  fromHand: true,
                  groupId: handPicked[child.tabloMemberId].photoId.startsWith(
                    'private',
                  )
                    ? // @ts-ignore
                      child.photo.groupId
                    : // @ts-ignore
                      sourcePhotosGroupedByPhotoId[child.photo?.photoId],
                  rotation: {
                    angle: {
                      roll: 0,
                      yaw: 0,
                      pitch: 0,
                    },
                  },
                  handCrop: handPicked[child.tabloMemberId].crop,
                },
              };
            }
            const kind = kindsGroupedByPerson[child.tabloMemberId];
            if (!kind) {
              return child;
            }
            const photo = kind.photos.sort((a, b) => {
              return (
                Math.abs(a.rotation?.angle.roll) +
                Math.abs(a.rotation?.angle.yaw) +
                Math.abs(a.rotation?.angle.pitch) -
                (Math.abs(b.rotation?.angle.roll) +
                  Math.abs(b.rotation?.angle.yaw) +
                  Math.abs(b.rotation?.angle.pitch))
              );
            })[0];

            return {
              ...child,

              photo,
            };
          })
          .map((v) => ({
            ...v,
            // @ts-ignore
            photo: v.photo?.photoId
              ? {
                  // @ts-ignore
                  ...v.photo,
                  // @ts-ignore
                  groupId: v.photo?.photoId?.startsWith('private')
                    ? // @ts-ignore
                      v.photo.groupId
                    : // @ts-ignore
                      sourcePhotosGroupedByPhotoId[v.photo?.photoId],
                }
              : // @ts-ignore
                v.photo,
          }));
      }),
    ),

    this.placeholders,
    this.sourcePhotos$,
    this.spineSnapKey$,
  ]).pipe(
    map(([v, namesList, placeholders]) => {
      const childItems: ChildLayoutItem[] = namesList.map(
        (child, index): ChildLayoutItem => ({
          ...child,
          type: 'child',
          sourceIndex: index + 1,
          index: index + 1,
          renderKey: `child-${child.tabloMemberId}`,
        }),
      );
      const placeholderItems = placeholders.map(createPlaceholderLayoutItem);
      const sortedItems = [...childItems, ...placeholderItems].sort(
        (a, b) => a.index - b.index,
      );
      const rows = sortedItems
        .reduce(groupIntoRows(v), [[]] as GroupSourceItem[][])
        .filter((row) => row.length > 0);

      if (v.addSpine && sortedItems.length > 0) {
        const compRect = this.elementRef.nativeElement.getBoundingClientRect();
        const compWidth = this.elementRef.nativeElement.clientWidth;
        const hostRect =
          this.elementRef.nativeElement.parentElement.parentElement.parentElement.getBoundingClientRect();
        const childHorizontalMargin = v.childHorizontalMargin ?? 0;
        const sideMargin = v.sideMargin ?? 0;
        const renderedScale = this.scale || 1;
        const childrenLeftOffset = sideMargin - childHorizontalMargin;
        const effectiveChildrenWidth =
          compWidth + 2 * childHorizontalMargin - 2 * sideMargin;

        const hostCentreX = hostRect.left + hostRect.width / 2;
        const centreInsideComp = (hostCentreX - compRect.left) / renderedScale;
        const itemWidth =
          (effectiveChildrenWidth - 2 * v.spineSize) / v.itemsPerRow;
        const centreInsideChildren = centreInsideComp - childrenLeftOffset;
        const targetSpineBoundary = Math.max(
          0,
          Math.min(
            v.itemsPerRow,
            (centreInsideChildren - v.spineSize) / itemWidth,
          ),
        );
        const spineBoundary = pickGlobalSpineBoundary(
          rows,
          v,
          targetSpineBoundary,
        );

        return buildGroupLayoutRows(sortedItems, v, {
          addSpine: true,
          spineBoundary,
        });
      }

      if (placeholderItems.length > 0) {
        return buildPaddedRows(rows, v);
      }

      // namesList.splice(5, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(6, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(9, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });
      // namesList.splice(10, 0, {
      //   firstName: null,
      //   lastName: null,
      //   isEmpty: true,
      // });

      return addRowNumbers(
        sortedItems
          .map((item, index) => ({
            ...item,
            index: item.type === 'placeholder' ? item.index : index + 1,
          }))
          .sort((a, b) => a.index - b.index),
        v,
      );
    }),
    tap((names) => this.updateExpectedImageLoadKeys(names)),
    shareReplay(1),
  );
  itemWidth$ = combineLatest([this.config$, this.names$]).pipe(
    map(([v, names]) => {
      if (v.addSpine) {
        return `calc(((100% - ${v.spineSize * 2}px) / ${v.itemsPerRow}) * var(--childWidth, 1))`;
      } else if (names.some((name) => name.type === 'placeholder')) {
        return `calc(${100 / v.itemsPerRow}% * var(--childWidth, 1))`;
      } else {
        return `calc(${100 / Math.min(v.itemsPerRow, names.length)}% * var(--childWidth, 1))`;
      }
    }),

    shareReplay(1),
  );
  namesWithoutSpine$ = this.names$.pipe(
    map((v) => v.filter((v) => !v.isSpine)),
  );
  itemHeight$ = combineLatest([this.config$, this.namesWithoutSpine$]).pipe(
    // tap((x) => console.log(x)),
    map(([v, namesList]) => 100 / namesList[0]?.rowsCount),
    shareReplay(1),
  );
  @Input() @HostBinding('class.disabled') disabled = false;

  @Input() @HostBinding('class.errored') get hasError() {
    return (
      this.isLeadingChild &&
      (!this.leadingChildId ||
        !this.namesList$.value.some(
          (v) => v.tabloMemberId === this.leadingChildId,
        ))
    );
  }

  @Input() ai = false;
  @Input() sourceImages!: ImageContainer[] | null;
  faceMatch$ = new BehaviorSubject<Record<string, TabloFaceToPerson>>({});

  @Input() set faceMatch(match: Record<string, TabloFaceToPerson>) {
    if (!match || this.context === 'teacher') {
      return;
    }
    this.faceMatch$.next(match);
    this.updateExpectedImageLoadKeys(this.renderedNames);
  }

  @Input() scale = 1;
  @Output() editCustomIndex = new EventEmitter<number>();
  @Input() @HostBinding('class.showSpine') showSpine = false;
  @Input() set spineSnapKey(value: unknown) {
    this.spineSnapKey$.next(value);
  }

  constructor(
    private fontSyncService: FontSyncService,
    private faceStrongDetectionService: FaceStrongDetectionService,
    private getPeopleInGroupGQL: GetPeopleInGroupGQL,
    private zalamoDialogService: ZalamoDialogService,
    private fontLoaderService: FontLoaderService,
    private organizationService: OrganizationService,
    private fullscreenService: FullscreenService,
    private elementRef: ElementRef,
    private uploadService: UploadService,
    private getPublicSessionOrganizationMembersGQL: GetPublicSessionOrganizationMembersGQL,
    private updatePublicSessionOrganizationMemberGQL: UpdatePublicSessionOrganizationMemberGQL,
    private injector: Injector,
  ) {
    super();
  }

  ngOnInit(): void {
    const updateRequested$ = new Subject<void>();

    this.subscribeWhileAlive(
      this.handPicked$.pipe(
        filter(() => !this.handPickedUpdatedFromTop),
        tap((v) => {
          this.handPickUpdated.emit(v);
        }),
      ),
      this.placeholders.pipe(
        filter(() => !this.placeHoldersUpdatedFromTop),
        tap((v) => {
          this.placeholdersUpdated.emit(v);
        }),
      ),
      this.getChildrenPhotos(),
      combineLatest([
        this.config$,
        this.names$.pipe(map((v) => v.filter((v) => v.type === 'child'))),
      ]).pipe(
        switchMap(([v, namesList]) => {
          updateRequested$.next(undefined);
          this.fontSizeFirstName.next(v.childFirstNameMaxFontSize);
          this.fontSizeLastName.next(v.childLastNameMaxFontSize);
          switch (v.nameHandling) {
            case 'split':
              this.fontSyncService
                .registerContext(`name-${this.context}`, namesList.length)

                .pipe(takeUntil(updateRequested$), takeUntil(this.ngOnDestroy$))

                .subscribe((v) => {
                  this.fontSizeLastName.next(v);
                });
              console.log('listen to fonts');
              this.fontSyncService
                .registerContext(`firstName-${this.context}`, namesList.length)
                .pipe(
                  takeUntil(updateRequested$.pipe(tap((x) => console.log(x)))),
                  takeUntil(this.ngOnDestroy$.pipe(tap((x) => console.log(x)))),
                )

                .subscribe(
                  (v) => {
                    this.fontSizeFirstName.next(v);
                  },
                  () => null,
                  () => {
                    console.log('completed');
                  },
                );
              this.namesListenerRegistered$.next(true);
              return this.fontSyncService.everythingCompleted().pipe(
                tap((v) => {
                  console.log('ALL COMPLETED');
                }),
              );
            case 'join':
            case 'firstNameOnly':
              this.fontSyncService
                .registerContext(`name-${this.context}`, namesList.length)
                .pipe(takeUntil(updateRequested$))
                .subscribe((v) => {
                  this.fontSizeFirstName.next(v);
                });
              this.fontSyncService
                .registerContext(
                  `roles-${this.context}`,
                  namesList.filter((v) => v.groupRoles?.length).length,
                )

                .pipe(takeUntil(updateRequested$), takeUntil(this.ngOnDestroy$))

                .subscribe((v) => {
                  this.fontSizeLastName.next(v);
                });
              this.namesListenerRegistered$.next(true);
              return this.fontSyncService.everythingCompleted().pipe(
                tap((v) => {
                  console.log('ALL COMPLETED');
                }),
              );
            case 'incognito':
              return of(null);
          }
        }),
      ),
    );
  }

  addPlaceHolder(index, size) {
    this.placeholders.next([
      ...this.placeholders.value.filter((v) => v.index < index),
      {
        index:
          index -
          1 +
          0.1 *
            (1 +
              this.placeholders.value.filter(
                (v) => v.index < index && v.index > index - 1,
              ).length),
        size,
      },
    ]);
  }

  removePlaceholder(index) {
    this.placeholders.next(
      this.placeholders.value.filter((v) => v.index !== index),
    );
  }

  getChildrenPhotos() {
    return combineLatest([
      this.sourcePhotos$,

      this.namesList$,
      // .pipe(
      // switchMap(
      //   (files) =>
      //     from(
      //       files
      //         .filter((v) => !!v.photoId)
      //         .map((v) => ({ ...v, photoId: v.publicSessionChildId })),
      //     ),
      // .pipe
      // mergeMap(({ photoId, firstName, lastName }) => {
      //   console.log(photoId);
      //   return this.faceStrongDetectionService
      //     .detectFacesByPhotoId(
      //       photoId,
      //       this.contextId,
      //       1,
      //       this.contextId,
      //       {
      //         width: 100,
      //         height: 100,
      //       },
      //       this.imagesContext,
      //     )
      //     .pipe(
      //       map((v) => ({
      //         face: v.faces[0],
      //         photoId,
      //         firstName,
      //         lastName,
      //       })),
      //     );
      // }),
      // bufferCount(files.filter((v) => !!v.photoId).length),
      // (),
      // ),
      // )
      this.ai
        ? this.sourceGroupIds$.pipe(
            switchMap((v) =>
              this.getPeopleInGroupGQL
                .watch({
                  variables: { photoGroupId: v },
                })
                .valueChanges.pipe(
                  throwOnQueryError,
                  filter(hasCompleteQueryData),
                ),
            ),
          )
        : of({ data: { getPeopleInGroup: [] } }),
      this.ai ? this.faceStrongDetectionService.init() : of(null),
    ]).pipe(
      // tap((x) => console.log(x)),
      switchMap(([files, sourceChildren, peopleInGroups]) =>
        from(files).pipe(
          mergeMap(({ photo: { photoId, width, height }, groupId }) => {
            // console.log(photoId);
            return this.ai
              ? this.faceStrongDetectionService
                  .detectFacesByPhotoId(
                    photoId,
                    groupId,
                    1,
                    this.contextId,
                    {
                      width,
                      height,
                    },
                    this.imagesContext,
                  )
                  .pipe(
                    map(({ faces }) => ({
                      // faces: faces.filter(
                      //   ({ boxRaw, rotation }) =>
                      //     boxRaw[2] > minWidth / 100 ||
                      //     boxRaw[3] > minHeight / 100
                      //   &&
                      //   Math.abs(rotation?.angle.yaw) < 0.35 &&
                      //   Math.abs(rotation?.angle.pitch) < 0.35 &&
                      //   Math.abs(rotation?.angle.roll) < 0.35
                      // ),
                      faces,
                      photoId,
                      groupId,
                      width,
                      height,
                    })),
                  )
              : Promise.resolve({
                  faces: [],
                  photoId,
                  groupId,
                  width,
                  height,
                });
          }, navigator.hardwareConcurrency),
          bufferCount(files.length),
          map((targets) => ({
            targets,
            sourceChildren: [...sourceChildren],
            peopleInGroups,
          })),
        ),
      ),

      tap(({ targets, sourceChildren, peopleInGroups }) => {
        if (!this.ai) {
          return null;
        }
        // this.photoToFaceMap.set(x.photoId, x.faces);
        const photoToFaceMap: Record<
          string,
          {
            face: {
              embedding: [];
              rotation: { angle: { roll: number; yaw: number; pitch: number } };
              boxRaw: number[];
            };
            photoId: string;
            groupId: string;
            width: number;
            height: number;
          }
        > = {};
        targets
          // .filter((x) => x.faces.length === 1)
          .forEach((x) => {
            photoToFaceMap[x.photoId] = {
              face: x.faces[0],
              photoId: x.photoId,
              groupId: x.groupId,
              width: x.width,
              height: x.height,
            };
          });
        const kidsToPhotos = peopleInGroups.data.getPeopleInGroup.reduce(
          (previousValue, currentValue) => {
            for (const person of currentValue.people) {
              if (!previousValue[person]) {
                previousValue[person] = [];
              }
              previousValue[person].push(currentValue.photoId);
            }
            return previousValue;
          },
          {} as Record<string, string[]>,
        );
        const groupedKinds = sourceChildren.reduce(
          (previousValue, currentValue) => {
            previousValue[currentValue.tabloMemberId] = {
              firstName: currentValue.firstName,
              lastName: currentValue.lastName,
              photos: kidsToPhotos[currentValue.tabloMemberId]
                ? kidsToPhotos[currentValue.tabloMemberId].map((photoId) => ({
                    ...photoToFaceMap[photoId],
                    rotation: photoToFaceMap[photoId].face?.rotation,
                    boxRaw: photoToFaceMap[photoId].face?.boxRaw,
                  }))
                : [],
            };

            return previousValue;
          },
          {} as Record<
            string,
            {
              firstName: string;
              lastName: string;
              photos: {
                photoId: string;
                rotation: any;
                boxRaw: number[];
                groupId: string;
                width: number;
                height: number;
              }[];
            }
          >,
        );

        this.kindsGroupedByPerson$.next(groupedKinds);
      }),
    );
  }

  trackChild(i, v) {
    return v.renderKey ?? v.tabloMemberId ?? `${v.type}-${v.index ?? i}-${i}`;
  }

  async editPhoto(
    name: {
      tabloMemberId: string;
      photo?: { photoId: string; groupId: string };
    },
    container: HTMLElement,
  ) {
    const imageContainer =
      container.querySelector('.photo-container').parentElement;

    const photos = await firstValueFrom(this.sourcePhotos$);

    if (photos.length === 0) {
      this.fullscreenService.open({
        data: { mode: 'warn' },
        content: 'Brak zdjęć do wybrania',
      });
      return;
    }
    const ref = this.zalamoDialogService.openRight(
      TabloSinglePhotoEditorComponent,
    );
    ref.componentInstance.tabloMember = name;
    if (name.photo?.photoId.startsWith('private')) {
      ref.componentInstance.photos = [
        {
          photo: {
            photoId: name.photo.photoId,
            groupId: name.photo.groupId,
          },
          groupId: name.photo.groupId,
        },
      ];
    } else {
      ref.componentInstance.photos = photos;
    }

    ref.componentInstance.saved
      .pipe(
        tap((v) =>
          this.handPicked$.next({
            ...this.handPicked$.value,
            [name.tabloMemberId]: {
              photoId: v.photoId,
              crop: v.crop,
            },
          }),
        ),
        tap(() => {
          ref.close();
        }),
      )

      .subscribe();
    ref.componentInstance.containerRatio =
      imageContainer.clientWidth / imageContainer.clientHeight;
    // ref.componentInstance.imageRatio = name.photo.width / name.photo.height;
    ref.componentInstance.contextId = this.contextId;
    ref.componentInstance.imagesContext = this.imagesContext;

    this.names$.pipe(first()).subscribe((names) => {
      console.log(name);
    });
  }

  imageLoaded(name: ChildLayoutItem) {
    const loadKey = this.getImageLoadKey(name);
    if (loadKey) {
      this.loadedImages.add(loadKey);
    }

    this.emitAllImagesLoadedIfReady();
  }

  private emitAllImagesLoadedIfReady(): void {
    const signature = this.getExpectedImageLoadSignature();
    if (this.imagesLoadedEmittedForSignature === signature) {
      return;
    }

    if (this.loadedImages.size >= this.expectedImageCount) {
      this.imagesLoadedEmittedForSignature = signature;
      this.allImagesLoaded.emit();
    }
  }

  private updateExpectedImageLoadKeys(names: GroupLayoutItem[]): void {
    this.renderedNames = names;
    const nextKeys = new Set(
      names
        .filter((name): name is ChildLayoutItem => name.type === 'child')
        .map((name) => this.getImageLoadKey(name))
        .filter((key): key is string => !!key),
    );
    const previousSignature = this.getExpectedImageLoadSignature();
    this.expectedImageLoadKeys = nextKeys;
    this.expectedImageCount = nextKeys.size;

    if (this.getExpectedImageLoadSignature() !== previousSignature) {
      this.loadedImages = new Set(
        Array.from(this.loadedImages).filter((key) => nextKeys.has(key)),
      );
      this.imagesLoadedEmittedForSignature = null;
    }

    this.emitAllImagesLoadedIfReady();
  }

  private getExpectedImageLoadSignature(): string {
    return Array.from(this.expectedImageLoadKeys).sort().join('|');
  }

  private getImageLoadKey(name: ChildLayoutItem): string | null {
    const photoId = this.getRenderablePhotoId(name);
    return photoId ? `${name.tabloMemberId}:${photoId}` : null;
  }

  private getRenderablePhotoId(name: ChildLayoutItem): string | null {
    const matchedPhoto = this.faceMatch$.value?.[name.tabloMemberId];
    const memberPhoto = (
      name as ChildLayoutItem & {
        photo?: { photoId?: string };
      }
    ).photo;

    return matchedPhoto?.photoId ?? memberPhoto?.photoId ?? null;
  }

  getMissingImageDebugDetails(): TabloGroupMissingImageDebug {
    const faceMatch = this.faceMatch$.value ?? {};
    const missingImages = this.renderedNames
      .filter((name): name is ChildLayoutItem => name.type === 'child')
      .filter((name) => {
        const loadKey = this.getImageLoadKey(name);
        return !loadKey || !this.loadedImages.has(loadKey);
      })
      .map((name) => {
        const photo = (
          name as ChildLayoutItem & {
            photo?: { photoId?: string; groupId?: string };
          }
        ).photo;
        const matchedPhoto = faceMatch[name.tabloMemberId];
        const memberPhotoId = photo?.photoId ?? name.photoId;
        const photoSource: 'faceMatch' | 'memberPhoto' | 'none' =
          matchedPhoto?.photoId
            ? 'faceMatch'
            : memberPhotoId
              ? 'memberPhoto'
              : 'none';

        return {
          tabloMemberId: name.tabloMemberId ?? null,
          firstName: name.firstName ?? null,
          lastName: name.lastName ?? null,
          photoId: matchedPhoto?.photoId ?? memberPhotoId ?? null,
          photoGroupId: matchedPhoto?.groupId ?? photo?.groupId ?? null,
          photoSource,
          sourceIndex: name.sourceIndex,
          row: name.row,
          itemIndex: name.itemIndex,
        };
      });

    return {
      groupElementId: this.groupElementId,
      groupType: this.context,
      contextId: this.contextId,
      imagesContext: this.imagesContext,
      expectedImages: this.expectedImageCount,
      loadedImages: this.loadedImages.size,
      missingImages,
    };
  }

  async uploadPhotoForTecher($event: Event, name: any) {}

  async editTeachers() {
    const { PublicSessionOrganizationsModule } =
      await import('../../../../../../../photographer-panel/src/app/modules/public-session-organizations/public-session-organizations.module');
    const injector = createInjector(
      PublicSessionOrganizationsModule,
      this.injector,
    );
    const carouselModule = injector.get(PublicSessionOrganizationsModule);
    carouselModule.editTeachers(
      this.publicSessionOrganizationId,
      this.originalContextId,
    );
  }
  async editTeacherRoles() {
    const { PublicSessionOrganizationsModule } =
      await import('../../../../../../../photographer-panel/src/app/modules/public-session-organizations/public-session-organizations.module');
    const injector = createInjector(
      PublicSessionOrganizationsModule,
      this.injector,
    );
    const carouselModule = injector.get(PublicSessionOrganizationsModule);
    carouselModule.editTeacherRoles(
      this.publicSessionOrganizationId,
      this.publicSessionOrganizationGroupId,
      this.originalContextId,
    );
  }
}
